
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ClientReportStatus struct
 */
class ClientReportStatus {
  /**
   * Initialize struct
   * @param {!number=} rolloverTimestamp
   * @param {!string=} filePath
   * @param {!boolean=} isSent
   * @param {!number=} reportTimestamp
   * @constructor
   */
  constructor (rolloverTimestamp = new UInt64(0, 0), filePath = '', isSent = false, reportTimestamp = new UInt64(0, 0)) {
    this.RolloverTimestamp = rolloverTimestamp
    this.FilePath = filePath
    this.IsSent = isSent
    this.ReportTimestamp = reportTimestamp
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ClientReportStatus}
   * @param {!ClientReportStatus} other Other struct
   * @returns {!ClientReportStatus} This struct
   */
  copy (other) {
    if (other.RolloverTimestamp != null) {
      this.RolloverTimestamp = UInt64.fromNumber(other.RolloverTimestamp)
    } else {
      this.RolloverTimestamp = null
    }
    if (other.FilePath != null) {
      this.FilePath = other.FilePath
    } else {
      this.FilePath = null
    }
    if (other.IsSent != null) {
      this.IsSent = other.IsSent
    } else {
      this.IsSent = null
    }
    if (other.ReportTimestamp != null) {
      this.ReportTimestamp = UInt64.fromNumber(other.ReportTimestamp)
    } else {
      this.ReportTimestamp = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ClientReportStatus}
   * @returns {!ClientReportStatus} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ClientReportStatusModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ClientReportStatusModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ClientReportStatus}
   * @param {!ClientReportStatus} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ClientReportStatus)) {
      throw new TypeError('Instance of ClientReportStatus is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ClientReportStatus}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      RolloverTimestamp: ((this.RolloverTimestamp != null) ? this.RolloverTimestamp.toNumber() : null),
      FilePath: ((this.FilePath != null) ? this.FilePath : null),
      IsSent: ((this.IsSent != null) ? this.IsSent : null),
      ReportTimestamp: ((this.ReportTimestamp != null) ? this.ReportTimestamp.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ClientReportStatus.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ClientReportStatus} other Object value
   * @returns {!ClientReportStatus} Created struct
   */
  static fromObject (other) {
    return new ClientReportStatus().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ClientReportStatus}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ClientReportStatus.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ClientReportStatus}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 3020
  }
}

export { ClientReportStatus };

/**
 * Fast Binary Encoding ClientReportStatus field model
 */
class FieldModelClientReportStatus extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.RolloverTimestamp = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.FilePath = new fbe.FieldModelString(buffer, this.RolloverTimestamp.fbeOffset + this.RolloverTimestamp.fbeSize)
    this.IsSent = new fbe.FieldModelBool(buffer, this.FilePath.fbeOffset + this.FilePath.fbeSize)
    this.ReportTimestamp = new fbe.FieldModelUInt64(buffer, this.IsSent.fbeOffset + this.IsSent.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelClientReportStatus}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelClientReportStatus}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.RolloverTimestamp.fbeSize + this.FilePath.fbeSize + this.IsSent.fbeSize + this.ReportTimestamp.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelClientReportStatus}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.RolloverTimestamp.fbeExtra + this.FilePath.fbeExtra + this.IsSent.fbeExtra + this.ReportTimestamp.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelClientReportStatus}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelClientReportStatus.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelClientReportStatus}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 3020
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelClientReportStatus}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelClientReportStatus}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.RolloverTimestamp.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RolloverTimestamp.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RolloverTimestamp.fbeSize

    if ((fbeCurrentSize + this.FilePath.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FilePath.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FilePath.fbeSize

    if ((fbeCurrentSize + this.IsSent.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsSent.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsSent.fbeSize

    if ((fbeCurrentSize + this.ReportTimestamp.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ReportTimestamp.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ReportTimestamp.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelClientReportStatus}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelClientReportStatus}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelClientReportStatus}
   * @param {!ClientReportStatus} fbeValue Default value, defaults is new ClientReportStatus()
   * @returns {!ClientReportStatus} ClientReportStatus value
   */
  get (fbeValue = new ClientReportStatus()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelClientReportStatus}
   * @param {!ClientReportStatus} fbeValue ClientReportStatus value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.RolloverTimestamp.fbeSize) <= fbeStructSize) {
      fbeValue.RolloverTimestamp = this.RolloverTimestamp.get()
    } else {
      fbeValue.RolloverTimestamp = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RolloverTimestamp.fbeSize

    if ((fbeCurrentSize + this.FilePath.fbeSize) <= fbeStructSize) {
      fbeValue.FilePath = this.FilePath.get()
    } else {
      fbeValue.FilePath = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FilePath.fbeSize

    if ((fbeCurrentSize + this.IsSent.fbeSize) <= fbeStructSize) {
      fbeValue.IsSent = this.IsSent.get()
    } else {
      fbeValue.IsSent = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsSent.fbeSize

    if ((fbeCurrentSize + this.ReportTimestamp.fbeSize) <= fbeStructSize) {
      fbeValue.ReportTimestamp = this.ReportTimestamp.get()
    } else {
      fbeValue.ReportTimestamp = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ReportTimestamp.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelClientReportStatus}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelClientReportStatus}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelClientReportStatus}
   * @param {!ClientReportStatus} fbeValue ClientReportStatus value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelClientReportStatus}
   * @param {ClientReportStatus} fbeValue ClientReportStatus value
   */
  setFields (fbeValue) {
    this.RolloverTimestamp.set(fbeValue.RolloverTimestamp)
    this.FilePath.set(fbeValue.FilePath)
    this.IsSent.set(fbeValue.IsSent)
    this.ReportTimestamp.set(fbeValue.ReportTimestamp)
  }
}

export { FieldModelClientReportStatus };

/**
 * Fast Binary Encoding ClientReportStatus model
 */
class ClientReportStatusModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelClientReportStatus(this.buffer, 4)
  }

  /**
   * Get the ClientReportStatus model
   * @this {!ClientReportStatusModel}
   * @returns {!FieldModelClientReportStatus} model ClientReportStatus model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ClientReportStatusModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ClientReportStatusModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ClientReportStatusModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ClientReportStatusModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelClientReportStatus.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ClientReportStatusModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ClientReportStatusModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ClientReportStatusModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ClientReportStatusModel}
   * @param {!ClientReportStatus} value ClientReportStatus value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ClientReportStatusModel}
   * @param {!ClientReportStatus} value ClientReportStatus value, defaults is new ClientReportStatus()
   * @return {!object} Deserialized ClientReportStatus value and its size
   */
  deserialize (value = new ClientReportStatus()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ClientReportStatus(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ClientReportStatus(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ClientReportStatusModel}
   * @param {!number} prev Previous ClientReportStatus model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ClientReportStatusModel };
